import {Alert, Typography, Box, useMediaQuery, Snackbar} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {useState, useEffect, useContext, useMemo} from 'react';
import {countries} from '../../HeHealth/countryLocations';

import ConfirmationDialog from '../../components/dialogbox/confirmationDialog';

import {MensSexualHealthBase} from './base';
// heHealth components
import {HeHealthInitial} from '../../components/heHealthDetails/heHealthInitial';
import {MultipleOptionQuestion} from '../../components/APIForms/multipleOptionQuestion';
import {CheckboxQuestion} from '../../components/APIForms/checkboxQuestion';
import {AutoCompleteSelect} from '../../components/APIForms/autoCompleteSelect';
import {UploadPicturesQuestionComponent} from '../../components/APIForms/uploadPhoto';
import {useHeHealthApi} from '../../api/hehealth';

// Contexts
import APIObjectsContext from '../../context/APIObjectsContext';
import APIServiceFormContext from '../../context/APIServiceFormContext';

import {useQuestionnaireApi} from '../../api/demographics';
import {useAssessmentApi} from '../../api/assessment';
import {AnimationLoadingScreenWithProgressBar} from '../../components/loading/loadingScreen';
import {delay} from '../../utils/miscellaneous';

//local dependencies(from this file)

//options that are passed to some question components
const condition_options = [
  {
    id: '1',
    alias: 'Penis pain',
    value: 'My penis is painful',
  },
  {
    id: '2',
    alias: 'Penis discharge',
    value: 'My penis has discharge',
  },
  {
    id: '3',
    alias: 'Urinary pain',
    value: 'I feel pain/burning sensation when urinating',
  },
  {
    id: '4',
    alias: 'Not experiencing symptoms.',
    value: 'None of the above',
    // value will be reassigned when shaping the object to be sent to hehealth, in that way we don't modify how the components work
  },
];
// this one is going to be processed before passing it to a question prop

const titleSX = {
  fontSize: 25,
  fontWeight: 650,
};

/** list of all questions keys and their respective components, important part for the heHealth form system
 *
 *  key:string // heHealths key of a required question
 *  name:string // not used yet
 *  component:React.Component // question component from `pages/dermaDetails/`
 *
 */
const questions = [
  {
    key: 'initial',
    component: <HeHealthInitial />,
  },
  {
    key: '3',
    component: <AutoCompleteSelect title="Where are you located?" titleSX={titleSX} options={countries} childSM={12} />,
  },
  {
    key: '4',
    component: (
      <CheckboxQuestion
        title="What are you experiencing?"
        subtitle=" Select all that apply."
        titleSX={titleSX}
        itemHeight={'60px'}
        childSM={5.5}
        choices={condition_options}
        additionaButOptionaValidation={selected => {
          let noAboveSymptomsDiscrepancy = selected.some(s => s.value === 'None of the above' && selected.length > 1);
          return noAboveSymptomsDiscrepancy
            ? `You can't select "None of the above" while selecting other options.`
            : undefined;
        }}
        apiName="hehealth"
      />
    ),
  },
  {
    key: 'image',
    component: (
      <UploadPicturesQuestionComponent
        title="Please upload a photo"
        subtitle="This wil help us give you accurate results. Please upload only one photo."
        apiHook={useHeHealthApi}
        useDropzoneProps={{
          maxSize: 5 * Math.pow(2, 20),
        }}
        processingImageCallback={(imageValidity, file, resolve, setImageURLs, useApiHook) => {
          const formData = new FormData();
          formData.append('image_file', file, file.name);

          useApiHook
            .postMensSexualHealthImageUrl(formData)
            .then(res => {
              const data = res.data;
              if (res.status === 200) {
                setImageURLs(prev => {
                  const output = [...prev];
                  output.push(data);
                  return output;
                });
                resolve(file); // Resolve the promise when finished
                return;
              }
            })
            .catch(e => {
              console.error('error: ', e);
            });
        }}
        dropzoneProps={{
          maxFiles: 1,
        }}
        maximumAllowedFiles={1}
      />
    ),
  },
  {
    key: '6',
    component: (
      <MultipleOptionQuestion
        title="When was the last time you had sexual contact? ⚡🔌"
        titleSX={titleSX}
        choices={[
          {id: '1', value: 'Within this month'},
          {id: '2', value: 'Within the last 1-3 months'},
          {id: '3', value: 'More than 3 months'},
          {id: '4', value: 'Never had sex'},
        ]}
        optionWidth={{xs: '100%', sm: '60%', md: '50%'}}
        apiName="hehealth"
      />
    ),
  },
];

// HEHEALTH FORM PAGE HELPER FUNCTIONS

/**
 * This function assumes that question dependencies have already been handled (and that the question key is the last question in the form). Depends on a fixed order of the questions
 *
 * @param {string} questionKey - input question key
 *
 * @returns {string} next question key
 */
const getNextQuestionBasedOnQuestionKey = questionKey => {
  if (questionKey === 'initial' || questionKey === '') return '3';

  if (questionKey === '3') return '4';
  if (questionKey === '4') return '6';

  if (questionKey === '6') return 'image';
  if (questionKey === 'image') return 'done';
};

/**
 * HeHealthFormPage component tracks the order of the questions and saves their data. However, some questions' answers can make the user go to additional questions that would otherwise be skipped, this object is used to manage those questions "dependencies" depending on their value.
 *
 * Each key here is the heHealth key that has a dependency, then each key has an object as a value which has two keys: the value, which is the value from that key that triggers the dependency(it can be any value or a function accepting a value for more customization on what to accept), and an array with the dependencies, this is an array of heHealth keys that will be shown if the value is as expected.
 */
let questionDependencies = {};

export const HehealthFormPage = () => {
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const [shouldRender, setShouldRender] = useState(false);
  const [responseReturned, setResponseReturned] = useState(false);
  const [diagnosisFinishedAnalysing, setDiagnosisFinishedAnalysing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const initialText = isSmallScreen ? 'Start' : 'Start my screening';
  const assessmentApi = useAssessmentApi();
  const questionnaireApi = useQuestionnaireApi();
  const heHealthApi = useHeHealthApi();

  const [confirmDialog, setConfirmDialog] = useState({
    title: '',
    subTitle: '',
    isOpen: false,
  });
  //way to display and handle validation
  const [errorMessage, setErrorMessage] = useState('');
  const [validationFunc, setValidationFunc] = useState(() => {
    function firstValidationFunc() {
      return '';
    }
    return firstValidationFunc;
  });

  const [sendDataArgs, setSendDataArgs] = useState({data: ''});

  const {
    caseObject,
    setCaseObject,
    caseObjectAdditionalDataValues: additionalDataValues,
    setCaseObjectAdditionalDataValues: setAdditionalDataValues,
    caseObjectQuestionKeysHistory: questionKeysHistory,
    setCaseObjectQuestionKeysHistory: setQuestionKeysHistory,
    userAssessmentID,
  } = useContext(APIObjectsContext);
  // an array that will be used as a stack to track the questions the user has seen, and the order of those
  const [questionHistoryIndex, setQuestionHistoryIndex] = useState(-1);

  /** total amount of questions that the user has to fill in. This updates based on which question the user answered considering the dependent questions and all */
  const totalQuestions = useMemo(() => {
    let totalQuestions = questions.length - 1;
    for (let question of Object.keys(questionDependencies)) {
      if (!questionKeysHistory.includes(question)) continue;

      if (
        caseObject[question] === undefined ||
        caseObject[question] === questionDependencies[question].value ||
        (typeof questionDependencies[question].value === 'function' &&
          questionDependencies[question].value(caseObject[question]))
      )
        continue;

      totalQuestions -= questionDependencies[question].dependencies.length;
    }
    return totalQuestions;
  }, [questionKeysHistory]);
  const [errorMessageOpen, setErrorMessageOpen] = useState(false);

  //depends on case object and additional data values
  const [savedValue, setSavedValue] = useState(undefined);
  const [additionalSavedData, setAdditionalSavedData] = useState(undefined);

  const [currentQuestion, setCurrentQuestion] = useState({
    name: '',
    question: '',
    type: '',
    key: '',
  });

  const [CurrentComponent, setCurrentComponent] = useState(
    questions[0].component, //initialComponent
  );

  useEffect(() => {
    if (validationFunc.instantError === undefined) return;

    setErrorMessage(validationFunc.instantError);
    setErrorMessageOpen(true);
  }, [validationFunc]);

  /* sets the first pieces of data of the caseObject based on the data we already have**/
  useEffect(() => {
    let firstCaseObject = {};
    questionnaireApi
      .getQuestionnaire()
      .then(res => {
        if (res.status !== 200) {
          return;
        }

        const data = res.data;
        // date of birth is in ISO format
        const formattedDate = data.date_of_birth.split('T')[0];

        firstCaseObject = {
          2: formattedDate,
        };

        assessmentApi
          .getAssessment(userAssessmentID)
          .then(res => {
            firstCaseObject.patientsNote = res.data.symptoms_description;
            let newCaseObject = {
              ...caseObject,
              ...firstCaseObject,
            };
            setCaseObject(newCaseObject);
            setShouldRender(true);
            setResponseReturned(true);
          })
          .catch(err => console.error(err));
      })
      .catch(e => {
        console.error('Error when trying to get questionnaire data: ', e);
        navigate('/demographics');
      }); // in case something goes wrong go to demographics page. This can avoid the errors of proceeding to the form without having a demographics first completed, which is important
  }, []);

  /* saves the data from each question to the caseObject and also saves the additional data. Both of those to have ready when contacting the API and for allowing the user to freely navigate back and forth between all the questions **/
  useEffect(() => {
    setSavedValue(caseObject[currentQuestion.key]);
    setAdditionalSavedData(additionalDataValues[currentQuestion.key]);
  }, [currentQuestion, caseObject, additionalDataValues]);

  useEffect(() => {
    if (currentQuestion.key === undefined || currentQuestion.key === '') return;
    setAdditionalDataValues(prev => ({
      ...prev,
      [currentQuestion.key]: additionalSavedData,
    }));
    handleVisibilityComponent(currentQuestion.key);
  }, [additionalSavedData, savedValue]);

  /**
   * Essential part of heHealths form system. This function will make sure to extract the current question's component data and put it where it should go.
   */
  function sendData() {
    function sendData1(data) {
      let newCaseObject = {
        ...caseObject,
        [currentQuestion.key]: data,
      };
      setCaseObject(newCaseObject);
      return newCaseObject;
    }

    function sendData2(data, keyHandling) {
      let staticKeyPart = keyHandling.staticParts;
      let changingPart =
        keyHandling.changingPart === undefined ? '' : currentQuestion.key.match(keyHandling.changingPart)[0];
      let newData = {};

      if (staticKeyPart !== undefined || staticKeyPart.length !== 0) {
        data.forEach((answer, idx) => {
          newData[changingPart + staticKeyPart[idx]] = answer;
        });
      } else {
        data.forEach(answer => {
          newData[changingPart] = answer;
        });
      }

      let newCaseObject = {...caseObject, ...newData};

      setCaseObject(newCaseObject);
      return newCaseObject;
    }
    const areArgumentsValid =
      arguments.length === 1 ||
      (arguments.length === 2 &&
        Array.isArray(arguments[0]) &&
        Array.isArray(arguments[1].staticParts) &&
        (arguments[1].changingPart instanceof RegExp || arguments[1].changingPart === undefined));

    if (!areArgumentsValid) {
      return;
    }

    let newCaseObject = {};

    if (arguments.length === 1) {
      newCaseObject = sendData1(arguments[0]);
    }
    if (
      arguments.length === 2 &&
      Array.isArray(arguments[0]) &&
      Array.isArray(arguments[1].staticParts) &&
      (arguments[1].changingPart instanceof RegExp || arguments[1].changingPart === undefined)
    ) {
      newCaseObject = sendData2(arguments[0], arguments[1]);
    }
    return newCaseObject;
  }

  const handleBack = () => {
    let lastQuestionKey = questionKeysHistory[questionHistoryIndex - 1];
    if (questionHistoryIndex === -1) {
      setConfirmDialog({
        title: 'Are you sure you want to cancel?',
        subTitle: 'If you cancel, you will lose your progress.',
        isOpen: true,
        onConfirm: () => {
          setConfirmDialog({
            ...confirmDialog,
            isOpen: false,
          });
          navigate('/');
        },
      });
      return;
    }

    //updating the object
    let newObject = caseObject;
    if (currentQuestion.key !== '') {
      newObject =
        sendDataArgs.keyHandling === undefined
          ? (newObject = sendData(sendDataArgs.data)) //first version of the function
          : (newObject = sendData(sendDataArgs.data, sendDataArgs.keyHandling));
    }
    if (sendDataArgs.additional !== undefined) {
      let newAdditionalDataValues = {
        ...additionalDataValues,
        [currentQuestion.key]: sendDataArgs.additional,
      };
      setAdditionalDataValues(newAdditionalDataValues);
    }

    if (questionHistoryIndex === 0) {
      setQuestionHistoryIndex(-1);
      setErrorMessage('');
      setCurrentQuestion({key: ''});
      handleVisibilityComponent('');
      return;
    }

    setQuestionHistoryIndex(questionHistoryIndex - 1);
    setCurrentQuestion({
      key: lastQuestionKey,
    });
    handleVisibilityComponent(lastQuestionKey);
  };

  /**
   * Based on a question key(assuming it is the last one the user is). Updates both the history of questions and the caseObject to manage any dependencies. For example, lets say that the user has filled all the questions and then returns to a question that was triggering a dependency and changes the value to another one that doesn't trigger a dependency. In that case this function will update the history to delete the dependant questions since they are not needed and also delete those keys from the caseObject. Then returns those 2 values in an object so they can be updated
   *
   * @param {string} questionKeyToCheck
   * @param {Object} currentCaseObject - Object following the caseObject guidelines from heHealth
   *
   * @returns {Object} Object with the updated caseObject and updated questionHistory too
   */
  const questionDependencyManagement = (questionKeyToCheck, currentCaseObject) => {
    if (!Object.keys(questionDependencies).includes(questionKeyToCheck)) return;
    const currentValue = currentCaseObject[questionKeyToCheck];
    const questionDependencyData = questionDependencies[questionKeyToCheck];
    const dependentQuestions = questionDependencyData.dependencies;

    if (
      (typeof questionDependencyData.value === 'function' && questionDependencyData.value(currentValue)) ||
      currentValue === questionDependencyData.value
    ) {
      let dependentsAlreadyIncludedInQuestionHistory = false;
      for (const question of dependentQuestions) {
        if (!questionKeysHistory.includes(question)) continue;

        dependentsAlreadyIncludedInQuestionHistory = true;
        break;
      }
      if (dependentsAlreadyIncludedInQuestionHistory) return;
      let newQuestionHistory = [
        ...questionKeysHistory.slice(0, questionHistoryIndex + 1),
        ...dependentQuestions.filter(q => !Array.isArray(q)),
        ...questionKeysHistory.slice(questionHistoryIndex + 1),
      ];
      setQuestionKeysHistory(newQuestionHistory);

      return {currentCaseObject, newQuestionHistory};
    }
    //if it is not equal to the value that generates the dependent questions, then the dependent questions must be deleted from the question history if they exist

    // assuming dependent questions are after the current question(and that current question value is the dependency)
    dependentQuestions.forEach(q => {
      if (!Array.isArray(q)) {
        delete currentCaseObject[q];
        return;
      }
      q.forEach(dependent => delete currentCaseObject[dependent]);
    });
    setCaseObject(currentCaseObject);

    let dependentQuestionIndexes = [];
    for (let i = questionHistoryIndex; i < questionKeysHistory.length; i++) {
      if (!dependentQuestions.includes(questionKeysHistory[i])) continue;
      dependentQuestionIndexes.push(i);
    }
    const newQuestionHistory = questionKeysHistory.filter((_, idx) => !dependentQuestionIndexes.includes(idx));
    setQuestionKeysHistory(newQuestionHistory);
    return {currentAnamnesisObject: currentCaseObject, newQuestionHistory};
  };

  const handleNext = () => {
    // validation
    let possibleErrorMessage = CurrentComponent.type === HeHealthInitial ? '' : validationFunc();
    setErrorMessage(possibleErrorMessage);
    if (possibleErrorMessage !== '') {
      setErrorMessageOpen(true);
      return;
    }

    let newObject = caseObject;
    if (currentQuestion.key !== '') {
      newObject =
        sendDataArgs.keyHandling === undefined
          ? (newObject = sendData(sendDataArgs.data)) //first version of the function
          : (newObject = sendData(sendDataArgs.data, sendDataArgs.keyHandling));
    }

    if (sendDataArgs.additional !== undefined) {
      let newAdditionalDataValues = {
        ...additionalDataValues,
        [currentQuestion.key]: sendData.additional,
      };
      setAdditionalDataValues(newAdditionalDataValues);
    }

    let newQuestionKeysHistory = questionKeysHistory;
    try {
      const {currentCaseObject, newQuestionHistory} = questionDependencyManagement(currentQuestion.key, newObject);
      newObject = currentCaseObject;
      newQuestionKeysHistory = newQuestionHistory;
    } catch {}

    //verify if we are in the last question from question history
    if (questionHistoryIndex < newQuestionKeysHistory.length - 1) {
      const newQuestionKey = newQuestionKeysHistory[questionHistoryIndex + 1];
      setCurrentQuestion({key: newQuestionKey});
      handleVisibilityComponent(newQuestionKey);
      setQuestionHistoryIndex(questionHistoryIndex + 1);
      return;
    }

    //Simulatiing Calling derma-api "http://0.0.0.0:8000/next_questions" endpoint
    const newKey = getNextQuestionBasedOnQuestionKey(currentQuestion.key);
    if (newKey === 'done') {
      setIsLoading(true);
      // here the object processing is done so that the object used during all the form can be transformed so it meets the criteria from the API
      let objectToSend = {};
      objectToSend.answers = [];
      // deleting keys that will be handled in a special way
      const newObjectKeys = Object.keys(newObject).filter(key => key !== 'patientsNote' && key !== 'image');
      objectToSend['patient_note'] = newObject['patientsNote'];
      objectToSend['image_url'] = newObject['image'][0];
      for (let key of newObjectKeys) {
        const questionId = parseInt(key);
        // modifying the question 6 if the user selects none of the above, assuming that if it selects that option then its the only element in that component
        if (questionId === 4 && newObject[key][0].id == 4) {
          newObject[key][0].value = 'No above symptoms';
        }
        let content = newObject[key];

        objectToSend['answers'].push({
          questionId,
          content,
        });
      }

      heHealthApi.postMensSexualHealthDiagnose(objectToSend).then(() => {
        setDiagnosisFinishedAnalysing(true);
        delay(2).then(() => {
          setIsLoading(false);
          navigate('/continue');
        });
      });
      return;
    }

    setCurrentQuestion({key: newKey});
    const presentQuestionKeys = Object.keys(newObject);
    let currentKeyHistory = questionKeysHistory.filter(key => presentQuestionKeys.includes(key));
    currentKeyHistory.push(newKey);
    setQuestionKeysHistory(currentKeyHistory);
    setQuestionHistoryIndex(currentKeyHistory.length - 1);
    handleVisibilityComponent(newKey);
  };

  // Update visibility of the main component(the question that is being shown)
  const handleVisibilityComponent = questionKey => {
    if (questionKey === '') {
      const initialComponent = questions.filter(q => q.key === 'initial')[0].component;
      setCurrentComponent(initialComponent);
      return;
    }
    if (questionKey === 'done') {
      navigate('/continue');
    }

    let question = questions.filter(comp => {
      if (comp.key instanceof RegExp) return questionKey.match(comp.key);

      return comp.key === questionKey;
    })[0]; //first index since there will only be one element
    if (question === undefined) question = '';
    setCurrentComponent(question.component);
  };

  return !shouldRender ? (
    <AnimationLoadingScreenWithProgressBar title="Loading..." secondsToLoad={30} isAlmostComplete={responseReturned} />
  ) : (
    <MensSexualHealthBase
      dontAnimate
      pageNumber={CurrentComponent === undefined || CurrentComponent.type !== HeHealthInitial ? 1 : 0}
      nextButtonName={
        CurrentComponent === undefined || CurrentComponent.type !== HeHealthInitial ? 'Next' : initialText
      }
      formProgress={((questionHistoryIndex + 1) / totalQuestions) * 95}
      backButtonName={CurrentComponent.type === HeHealthInitial ? 'Cancel' : 'Back'}
      backButtonIcon={CurrentComponent.type === HeHealthInitial ? '' : undefined}
      handleBack={handleBack}
      handleNext={handleNext}
    >
      <APIServiceFormContext.Provider
        value={{
          setValidationFunc,
          setSendDataArgs,
          savedValue,
          additionalSavedData,
        }}
      >
        {isLoading && (
          <AnimationLoadingScreenWithProgressBar
            title="Analyzing data..."
            secondsToLoad={30}
            isAlmostComplete={diagnosisFinishedAnalysing}
          />
        )}
        <Box
          sx={{
            width: 'fit-content',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          {CurrentComponent.type !== HeHealthInitial && (
            <Typography my={3}>
              We know some of the following questions can be personal, but they help us give you accurate results and
              appropiate recommendations.
            </Typography>
          )}
          {CurrentComponent}

          {errorMessage !== '' && (
            <Snackbar
              autoHideDuration={5000}
              open={errorMessageOpen}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              onClose={() => {
                setErrorMessageOpen(false);
              }}
            >
              <Alert
                severity="error"
                sx={{
                  '& .MuiSvgIcon-root': {
                    fontSize: 20,
                  },
                  display: 'flex',
                  alignItems: 'center',
                  textAlign: 'center',
                  mb: 8,
                  fontSize: {
                    xs: 16,
                    sm: 18,
                  },
                  px: {
                    xs: 2,
                    sm: 5,
                  },
                  borderRadius: 3,
                }}
              >
                {errorMessage.charAt(errorMessage.length - 1) === '.' ? errorMessage : errorMessage + '.'}
              </Alert>
            </Snackbar>
          )}
        </Box>
      </APIServiceFormContext.Provider>
      {/* Confirmation dialog box when user clicks Cancel button on footer */}
      <ConfirmationDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
    </MensSexualHealthBase>
  );
};
